// CUÁNTO SE QUEDA LA GENTE — y si eso explica lo que se factura.
//
// Este bloque existe para separar dos problemas que en la pantalla vieja se ven
// EXACTAMENTE IGUAL: "facturo menos porque viene menos gente" y "facturo menos
// porque estoy cobrando mal". Los dos bajan la misma barra de ingresos, pero uno
// se arregla con marketing y el otro con el tarifario. Cruzar permanencia contra
// ticket promedio es lo único que los distingue, y por eso los dos números van
// juntos y nunca sueltos.
//
// Tres decisiones de cálculo que no hay que "simplificar":
//
// · LA PERMANENCIA SE CALCULA POR VEHÍCULO, NO POR DÍA. Es Σ stay.total_hours
//   dividido Σ tickets.total.out, no el promedio de los `stay.avg_hours`
//   diarios. La pregunta es "cuánto se queda un vehículo", así que la unidad es
//   el vehículo. Promediar los promedios diarios le da el mismo peso a un
//   domingo de 4 salidas que a un viernes de 300, y con eso el domingo termina
//   gobernando el número. La regla general de esta pantalla es "mediana, nunca
//   promedio" porque un día extraordinario corre el promedio: acá la protección
//   contra ese día es la ponderación por salidas, que es lo mismo pero para un
//   cociente.
//
// · LOS DÍAS SIN SALIDAS NO CUENTAN. Ni para la permanencia, ni para el ticket
//   promedio, ni para el rango. Un día cerrado no es un día de 0 h de estadía:
//   es un día del que no se sabe nada. Contarlo hunde el promedio y le echa la
//   culpa al negocio de haber estado cerrado.
//
// · LAS HORAS Y LAS SALIDAS SE SUMAN DEL MISMO DÍA O DE NINGUNO. Hay clientes
//   con `stay.total_hours` en cero y `avg_hours` cargado, y viceversa. Si se
//   suman las horas de unos días y las salidas de todos, el cociente da de
//   menos y nadie se entera. Por eso `horasDelDia` devuelve null cuando el día
//   no tiene con qué, y ese día queda afuera de las dos sumas a la vez.
//
// Y una limitación del contrato de datos que conviene tener presente: el
// documento MENSUAL no lleva `stay`. La permanencia sólo existe en los diarios,
// así que este bloque nunca puede ir más atrás que la ventana de diarios
// disponible, por más que el selector diga "12 meses".

import React, { useMemo } from "react";
import { Row, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { SIN_LEYENDA } from "../Stats/graficos";
import Bloque from "./Bloque";
import { ORIGEN, UI, POLARIDAD } from "./theme";
import { formatCurrency, horas, fecha, entero, variacion, variacionTexto } from "./format";

// Color POR CONCEPTO: permanencia es "otros" (tiempo, no plata de tickets) y
// ticket promedio es el azul de Tickets. Van en dos gráficos separados a
// propósito: son horas contra pesos, y compartir eje sería una mentira visual.
const COLOR_PERMANENCIA = ORIGEN.otros.color;
const COLOR_TICKET = ORIGEN.tickets.color;

// Debajo de este porcentaje la diferencia se declara "igual". No es una cifra
// caprichosa: las dos cuentas son cocientes sobre muestras de días, y con
// ventanas de 7 a 30 días un movimiento de 3 o 4 puntos se explica por qué días
// cayeron adentro de la ventana. Llamarlo "subió" sería fingir precisión.
const UMBRAL_IGUAL = 5;

// Mínimos para comparar. Con menos que esto la comparación es ruido, y esta
// pantalla prefiere no decir nada antes que inventar una tendencia.
const MIN_DIAS = 4;
const MIN_SALIDAS = 10;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const salidasDe = (d) => num(d?.tickets?.total?.out);

/**
 * Horas de estadía acumuladas en un día, o null si el día no las tiene.
 *
 * El null es la parte importante: marca "de este día no sé", que es distinto de
 * "este día dio cero". Quien lo cambie por un 0 va a hacer que la permanencia
 * baje sola en los clientes que todavía no actualizaron el Desktop.
 */
const horasDelDia = (d) => {
  const out = salidasDe(d);
  if (out <= 0) return null;
  const total = num(d?.stay?.total_hours);
  if (total > 0) return { horas: total, out, reconstruido: false };
  // Respaldo: el promedio del día por las salidas del día. Es el mismo número
  // que `total_hours` cuando el Desktop lo manda bien, y es lo mejor que hay
  // cuando manda sólo el promedio.
  const avg = num(d?.stay?.avg_hours);
  if (avg > 0) return { horas: avg * out, out, reconstruido: true };
  return null;
};

/** Los dos números de una ventana, más lo que hace falta para rotularlos. */
function resumenVentana(docs) {
  let horasTotales = 0;
  let salidasConHoras = 0;
  let diasConHoras = 0;
  let reconstruidos = 0;
  let monto = 0;
  let salidas = 0;
  let dias = 0;

  docs.forEach((d) => {
    const out = salidasDe(d);
    if (out <= 0) return; // día sin movimiento: no entra en ninguna cuenta
    dias += 1;
    salidas += out;
    monto += num(d?.tickets?.total?.amount);

    const h = horasDelDia(d);
    if (h) {
      horasTotales += h.horas;
      salidasConHoras += h.out;
      diasConHoras += 1;
      if (h.reconstruido) reconstruidos += 1;
    }
  });

  if (!dias) return null;

  return {
    dias,
    salidas,
    monto,
    // Cociente sobre las MISMAS salidas de las que se tienen horas.
    permanencia: salidasConHoras > 0 ? horasTotales / salidasConHoras : null,
    diasConHoras,
    salidasConHoras,
    reconstruidos,
    ticket: salidas > 0 ? monto / salidas : null,
    desde: docs[0]?.date || null,
    hasta: docs[docs.length - 1]?.date || null,
  };
}

/** "sube" | "igual" | "baja" | null. El null significa "no hay con qué". */
const clasificar = (v) => {
  if (v === null || !Number.isFinite(v)) return null;
  if (v > UMBRAL_IGUAL) return "sube";
  if (v < -UMBRAL_IGUAL) return "baja";
  return "igual";
};

/**
 * Cómo se nombra el período anterior dentro de la frase.
 *
 * La especificación fija "que el mes pasado" para la ventana de 30 días. Con la
 * de 7 esa misma frase sería falsa, así que el rótulo acompaña a la ventana.
 */
/**
 * Variacion + contra que se compara, en una frase que se lee.
 *
 * variacionTexto(0) devuelve "sin cambios", que pegado a rotuloPrevia daba
 * **"sin cambios que el mes pasado"** — y ese es el caso MAS FRECUENTE del
 * bloque, no un borde raro.
 */
const textoComparacion = (v, n) => {
  const r = v === null || v === undefined || !Number.isFinite(v) ? null : Math.round(v);
  if (r === null) return "—";
  if (r === 0) return `sin cambios respecto de ${rotuloPrevia(n).replace(/^que /, "")}`;
  return `${variacionTexto(v)} ${rotuloPrevia(n)}`;
};

const rotuloPrevia = (n) => {
  if (n <= 7) return "que la semana anterior";
  if (n <= 31) return "que el mes pasado";
  return "que el período anterior";
};

/**
 * Las nueve frases del cruce permanencia × ticket promedio.
 *
 * Función pura y sin JSX a propósito: es lo único de este archivo que se puede
 * probar sin montar React, y es lo que hay que revisar cuando alguien discute
 * una redacción. Si este cruce no puede producir una frase, el bloque no se
 * dibuja: un número sin veredicto al lado es exactamente lo que esta pantalla
 * vino a eliminar.
 */
function frasePermanencia({ cPerm, cTicket, permanencia, dTicket, dPerm, previa }) {
  const pT = Math.abs(Math.round(dTicket));
  const pP = Math.abs(Math.round(dPerm));

  if (cPerm === "igual" && cTicket === "baja") {
    return `La gente se queda igual ${previa} (${horas(permanencia)}) pero el ticket promedio bajó ${pT}%. `
      + "Eso no es la demanda: revisa cómo se está cobrando.";
  }
  if (cPerm === "baja" && cTicket === "baja") {
    return "Se quedan menos y pagan menos. Es menos demanda, no un problema de cobro.";
  }
  if (cPerm === "sube" && cTicket === "sube") {
    return "Se quedan más y pagan más. Va bien.";
  }
  if (cPerm === "sube" && cTicket === "igual") {
    return "Se quedan más pero pagas lo mismo. Fíjate si la tarifa por hora se está quedando corta.";
  }
  // ── Los cinco cruces que la especificación no redacta, en la misma voz ──
  if (cPerm === "igual" && cTicket === "sube") {
    return `La gente se queda igual ${previa} (${horas(permanencia)}) y el ticket promedio subió ${pT}%. `
      + "El cambio está en la tarifa, no en la demanda.";
  }
  if (cPerm === "igual" && cTicket === "igual") {
    return `Se quedan lo mismo ${previa} y cada vehículo deja lo mismo. Nada se movió: lo que cambie tu `
      + "facturación este mes va a ser por cuántos entraron, no por cuánto se quedaron.";
  }
  if (cPerm === "baja" && cTicket === "igual") {
    return `Se quedan ${pP}% menos pero cada vehículo paga lo mismo. Estás cobrando igual por menos tiempo: `
      + "revisa si la tarifa mínima está haciendo todo el trabajo.";
  }
  if (cPerm === "baja" && cTicket === "sube") {
    return "Se quedan menos y aun así pagan más. La suba viene de la tarifa, no de más demanda.";
  }
  if (cPerm === "sube" && cTicket === "baja") {
    return "Se quedan más y aun así pagan menos. Eso no se explica por la demanda: revisa cómo se está cobrando.";
  }
  return null;
}

/** El titular: la conclusión del cruce, con el número que la sostiene. */
function titularPermanencia({ cPerm, cTicket, dPerm, dTicket }) {
  const pT = Math.abs(Math.round(dTicket));
  const pP = Math.abs(Math.round(dPerm));
  const mapa = {
    "igual|baja": `El ticket promedio bajó ${pT}% sin que la gente se quede menos`,
    "igual|sube": `El ticket promedio subió ${pT}% con la misma permanencia`,
    "igual|igual": "Ni la permanencia ni el ticket promedio se movieron",
    "baja|baja": "Se quedan menos y te dejan menos",
    "baja|igual": `Se quedan ${pP}% menos y el ticket promedio no se movió`,
    "baja|sube": "Se quedan menos pero cada vehículo te deja más",
    "sube|sube": "Se quedan más y te dejan más",
    "sube|igual": `Se quedan ${pP}% más y te dejan lo mismo`,
    "sube|baja": "Se quedan más y aun así te dejan menos",
  };
  return mapa[`${cPerm}|${cTicket}`] || null;
}

/** Configuración base de un sparkline. Sale de CurvaDelDia: sin toolbar ni zoom. */
function opcionesSpark(color, formateador) {
  return {
    chart: {
      type: "line",
      sparkline: { enabled: true },
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
      animations: { enabled: false },
    },
    colors: [color],
    stroke: { curve: "smooth", width: 2 },
    // 30 puntos en 60 px: cualquier etiqueta de dato se pisa con la de al lado.
    dataLabels: { enabled: false },
    // Una sola línea de 60 px de alto, pegada debajo del número que ilustra:
    // una leyenda sería más grande que el gráfico y diría lo que ya dice el
    // rótulo de la métrica.
    legend: SIN_LEYENDA,
    grid: { borderColor: UI.linea, strokeDashArray: 3, padding: { top: 4, bottom: 4, left: 0, right: 0 } },
    // Eje de tiempo real, no de índices: así un día sin movimiento se ve como un
    // hueco y no como si el de al lado fuera el día siguiente.
    xaxis: { type: "datetime" },
    tooltip: {
      fixed: { enabled: false },
      x: { format: "dd/MM/yyyy" },
      y: { title: { formatter: () => "" }, formatter: formateador },
      marker: { show: false },
    },
  };
}

/** Un número grande con su rótulo, su comparación y su línea de tendencia. */
const Metrica = ({ rotulo, valor, color, comparacion, serie, formateador, ariaLabel }) => (
  <div>
    <div style={{ fontSize: 11, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {rotulo}
    </div>
    <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.15, marginTop: 2 }}>
      {valor}
    </div>
    <div style={{ fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.4, minHeight: 18 }}>
      {comparacion}
    </div>
    {serie && serie.length >= 3 && (
      // En el teléfono las dos columnas se apilan y cada línea queda a ancho
      // completo, que es donde mejor se lee.
      <div style={{ marginTop: 6 }} aria-label={ariaLabel}>
        <ReactApexChart
          options={opcionesSpark(color, formateador)}
          series={[{ name: rotulo, data: serie }]}
          type="line"
          height={60}
        />
      </div>
    )}
  </div>
);

/** Una fila del detalle: concepto, ventana actual, ventana anterior. */
const Fila = ({ concepto, ahora, antes }) => (
  <div
    className="d-flex flex-wrap"
    style={{ padding: "7px 0", borderBottom: `1px solid ${UI.linea}`, fontSize: 13, gap: 8 }}
  >
    <div style={{ flex: "1 1 160px", color: UI.textoSuave }}>{concepto}</div>
    <div style={{ flex: "0 0 130px", color: UI.texto, fontWeight: 600 }}>{ahora}</div>
    <div style={{ flex: "0 0 130px", color: UI.textoSuave }}>{antes}</div>
  </div>
);

/**
 * El estado vacío también es una respuesta: dice qué falta y cómo se consigue.
 *
 * El titular NO puede ser "Cuánto se queda la gente": eso nombra el tema y deja
 * al dueño mirando una tarjeta sin saber si el dato no existe, no llegó o no se
 * puede calcular todavía. Cada estado vacío trae su propio titular.
 */
const Vacio = ({ titular, frase }) => (
  <Bloque nombre="Cuánto se queda la gente" id="cuanto-se-queda" titular={titular} frase={frase} />
);

const CuantoSeQueda = ({ cerrados = [], periodo = "30d" }) => {
  const datos = useMemo(() => {
    // El selector de período gobierna este bloque, pero la permanencia sólo vive
    // en los documentos diarios: "12 meses" se atiende con todos los diarios que
    // haya, no con los mensuales, que no traen `stay`.
    const N = periodo === "7d" ? 7 : periodo === "12m" ? 365 : 30;

    // Regla 4 de la pantalla: los días sin operar quedan afuera ANTES de armar
    // las ventanas. Si se filtraran después, "los últimos 30" podrían ser 30
    // casilleros con 9 días cerrados adentro, y las dos ventanas dejarían de ser
    // comparables entre sí.
    const conMovimiento = (cerrados || []).filter((d) => salidasDe(d) > 0);
    if (!conMovimiento.length) return { vacio: "sin_dias" };

    const actualDocs = conMovimiento.slice(-N);
    const previaDocs = conMovimiento.slice(
      Math.max(0, conMovimiento.length - 2 * N),
      Math.max(0, conMovimiento.length - N),
    );

    const actual = resumenVentana(actualDocs);
    const previa = resumenVentana(previaDocs);

    if (!actual || actual.permanencia === null) return { vacio: "sin_permanencia" };

    const hayPrevia = !!(
      previa
      && previa.permanencia !== null
      && previa.dias >= MIN_DIAS
      && previa.salidas >= MIN_SALIDAS
      && actual.dias >= MIN_DIAS
      && actual.salidas >= MIN_SALIDAS
    );

    const dPerm = hayPrevia ? variacion(actual.permanencia, previa.permanencia) : null;
    const dTicket = hayPrevia ? variacion(actual.ticket, previa.ticket) : null;
    const cPerm = clasificar(dPerm);
    const cTicket = clasificar(dTicket);

    const frase = cPerm && cTicket
      ? frasePermanencia({
        cPerm,
        cTicket,
        dPerm,
        dTicket,
        permanencia: actual.permanencia,
        previa: rotuloPrevia(N),
      })
      : null;

    if (!frase) return { vacio: "sin_comparacion", dias: conMovimiento.length, N };

    // La tendencia se dibuja sobre la ventana elegida, con techo de 30 puntos:
    // más que eso en 60 px de alto es una mancha, no una línea.
    const puntos = actualDocs.slice(-30);
    const serieHoras = [];
    const serieTicket = [];
    puntos.forEach((d) => {
      const ts = new Date(`${d.date}T00:00:00`).getTime();
      if (!Number.isFinite(ts)) return;
      const h = horasDelDia(d);
      if (h) serieHoras.push({ x: ts, y: Math.round((h.horas / h.out) * 100) / 100 });
      const out = salidasDe(d);
      const monto = num(d?.tickets?.total?.amount);
      if (out > 0 && monto > 0) serieTicket.push({ x: ts, y: Math.round(monto / out) });
    });

    return {
      actual,
      previa,
      dPerm,
      dTicket,
      frase,
      N,
      titular: titularPermanencia({ cPerm, cTicket, dPerm, dTicket }),
      serieHoras,
      serieTicket,
    };
  }, [cerrados, periodo]);

  if (datos.vacio === "sin_dias" || datos.vacio === "sin_permanencia") {
    // Dos faltantes distintos, y al dueño le cambia qué hacer: uno se arregla
    // operando, el otro actualizando la PC. Un mismo texto para los dos lo
    // mandaría a resincronizar cuando lo único que pasa es que todavía no cobró
    // ninguna salida.
    const esSinDias = datos.vacio === "sin_dias";
    return (
      <Vacio
        titular={
          esSinDias
            ? "Todavía no hay salidas cobradas para medir cuánto se queda la gente"
            : "El tiempo de estadía todavía no llega desde tu PC"
        }
        frase={
          esSinDias
            ? "Se calcula sobre los vehículos que salieron, y todavía no hay ninguno. Aparece solo con la primera jornada que TicketControl cierre con salidas."
            : "Este dato empieza a calcularse con la versión 2.0.69 de TicketControl. Si ya actualizaste, se completa solo: cada jornada que cierra suma la suya, y en una semana ya se ve la tendencia."
        }
      />
    );
  }

  if (datos.vacio === "sin_comparacion") {
    // Los dos números existen, pero sin período anterior no hay veredicto — y un
    // número sin veredicto no se muestra. Se dice qué falta y cuánto falta.
    const faltan = Math.max(1, datos.N * 2 - datos.dias);
    return (
      <Vacio
        titular="Todavía no puedo decirte si la gente se queda más o menos que antes"
        frase={
          `Para comparar dos períodos hacen falta ${datos.N * 2} días con movimiento y hay `
          + `${datos.dias}: faltan ${faltan} ${faltan === 1 ? "día" : "días"} de operación. Se completa `
          + "solo, cada jornada que TicketControl cierra suma uno."
        }
      />
    );
  }

  const { actual, previa, dPerm, dTicket } = datos;

  return (
    <Bloque
      nombre="Cuánto se queda la gente"
      id="cuanto-se-queda"
      titular={datos.titular}
      frase={datos.frase}
      acento={COLOR_PERMANENCIA}
      rango={
        `${fecha(actual.desde)} – ${fecha(actual.hasta)} · ${actual.dias} `
        + `${actual.dias === 1 ? "día" : "días"} con movimiento · `
        + `${entero(actual.salidas)} vehículos que salieron`
      }
      detalleLabel="Ver cómo se calcula"
      detalle={(
        <div>
          <Fila concepto="" ahora="Este período" antes="Anterior" />
          <Fila
            concepto="Permanencia por vehículo"
            ahora={horas(actual.permanencia)}
            antes={previa && previa.permanencia !== null ? horas(previa.permanencia) : "—"}
          />
          <Fila
            concepto="Ticket promedio"
            ahora={formatCurrency(actual.ticket)}
            antes={previa && previa.ticket !== null ? formatCurrency(previa.ticket) : "—"}
          />
          <Fila
            concepto="Vehículos que salieron"
            ahora={entero(actual.salidas)}
            antes={previa ? entero(previa.salidas) : "—"}
          />
          <Fila
            concepto="Días con movimiento"
            ahora={entero(actual.dias)}
            antes={previa ? entero(previa.dias) : "—"}
          />
          <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "10px 0 0", lineHeight: 1.5 }}>
            La permanencia es el total de horas dividido el total de vehículos que salieron, no el
            promedio de los promedios diarios: así un día de pocas salidas no pesa lo mismo que uno
            lleno. El ticket promedio es lo cobrado en tickets dividido esos mismos vehículos. Los
            días sin movimiento no entran en ninguna de las dos cuentas.
          </p>
          {actual.diasConHoras < actual.dias && (
            <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "8px 0 0", lineHeight: 1.5 }}>
              {actual.dias - actual.diasConHoras} de los {actual.dias} días no traen el tiempo de
              estadía y quedaron afuera del cálculo de permanencia. El ticket promedio sí los incluye.
            </p>
          )}
          {actual.reconstruidos > 0 && (
            <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "8px 0 0", lineHeight: 1.5 }}>
              En {actual.reconstruidos} {actual.reconstruidos === 1 ? "día" : "días"} el total de horas
              no vino y se reconstruyó desde el promedio del día. Ese tramo es estimado.
            </p>
          )}
        </div>
      )}
    >
      <Row className="g-3">
        <Col xs={12} sm={6}>
          <Metrica
            rotulo="Se quedan"
            valor={horas(actual.permanencia)}
            color={COLOR_PERMANENCIA}
            // La permanencia NO lleva color de polaridad: más horas no es "bueno"
            // por sí solo —en una playa de rotación es justamente lo contrario—.
            // Para qué lado juega lo dice la frase de arriba, no un verde.
            comparacion={(
              <>
                {textoComparacion(dPerm, datos.N)}
                {previa && previa.permanencia !== null ? ` (${horas(previa.permanencia)})` : ""}
              </>
            )}
            serie={datos.serieHoras}
            formateador={(v) => horas(v)}
            ariaLabel="Tendencia de la permanencia por día"
          />
        </Col>
        <Col xs={12} sm={6}>
          <Metrica
            rotulo="Y te dejan"
            valor={formatCurrency(actual.ticket)}
            color={COLOR_TICKET}
            comparacion={(
              <>
                <span
                  style={{
                    color: dTicket >= 0 ? POLARIDAD.entra.color : POLARIDAD.sale.color,
                    fontWeight: 600,
                  }}
                >
                  {variacionTexto(dTicket)}
                </span>
                {" "}
                {Math.round(dTicket) === 0
                  ? `respecto de ${rotuloPrevia(datos.N).replace(/^que /, "")}`
                  : rotuloPrevia(datos.N)}
                {previa && previa.ticket !== null ? ` (${formatCurrency(previa.ticket)})` : ""}
              </>
            )}
            serie={datos.serieTicket}
            formateador={(v) => formatCurrency(v)}
            ariaLabel="Tendencia del ticket promedio por día"
          />
        </Col>
      </Row>
      <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "10px 0 0", lineHeight: 1.45 }}>
        Cada vehículo que salió, en promedio: cuánto tiempo estuvo adentro y cuánto dejó.
        Los abonados y los convenios no entran en esta cuenta.
      </p>
    </Bloque>
  );
};

export default CuantoSeQueda;
